import { useState, useEffect } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { familyInvitationService } from '../../lib/family-invitation-service';
import { UserPlus, Mail, MessageSquare, Copy, Calendar, Clock, X, Send, CheckCircle, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import InvitationForm from './InvitationForm';

export default function FamilyInvitations() {
  const { family, currentUser, user } = useFamilyContext();
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [invitingMember, setInvitingMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadInvitations();
  }, [family]);

  const loadInvitations = async () => {
    try {
      setLoading(true);
      const data = await familyInvitationService.getFamilyInvitations();
      setInvitations(data);
    } catch (error) {
      console.error('Error loading invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvitation = async (inviteData) => {
    setSubmitting(true);
    
    try {
      // Find the current user's family member ID  
      const userId = currentUser?.authUserId || user?.id;
      const currentMember = (family.parents || []).find(p => p.authUserId === userId) ||
                           (family.kids || []).find(k => k.authUserId === userId);
      
      if (!currentMember) {
        throw new Error(`Could not find your family member profile. UserId: ${userId}`);
      }

      const invitation = await familyInvitationService.createInvitation(
        currentMember.id,
        { ...inviteData, familyName: family.name }
      );

      // Send invitation via SMS if phone provided
      if (inviteData.phone) {
        await familyInvitationService.sendInvitationSMS(
          invitation,
          family.name,
          currentUser?.name || family.parentName
        );
      }

      // Send invitation via email if email provided
      if (inviteData.email) {
        await familyInvitationService.sendInvitationEmail(
          invitation,
          family.name,
          currentUser?.name || family.parentName
        );
      }

      setInvitations(prev => [invitation, ...prev]);
      alert(`Invitation created for ${inviteData.name}! Share the code: ${invitation.invite_code}`);
      
    } catch (error) {
      console.error('Error creating invitation:', error);
      throw error; // Re-throw so the form can handle it
    } finally {
      setSubmitting(false);
    }
  };

  const copyInviteCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      // You could show a toast notification here
      console.log('Invite code copied to clipboard');
    } catch (error) {
      console.error('Failed to copy invite code:', error);
    }
  };

  const cancelInvitation = async (invitationId) => {
    if (!confirm('Are you sure you want to cancel this invitation?')) return;
    
    try {
      await familyInvitationService.cancelInvitation(invitationId);
      setInvitations(prev => 
        prev.map(inv => 
          inv.id === invitationId ? { ...inv, status: 'cancelled' } : inv
        )
      );
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      alert('Error cancelling invitation');
    }
  };

  const handleInviteExistingMember = (member) => {
    setInvitingMember(member);
  };

  const resendInvitation = async (invitationId) => {
    try {
      const updatedInvitation = await familyInvitationService.resendInvitation(invitationId);
      setInvitations(prev =>
        prev.map(inv =>
          inv.id === invitationId ? updatedInvitation : inv
        )
      );
    } catch (error) {
      console.error('Error resending invitation:', error);
      alert('Error resending invitation');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'accepted': return 'bg-emerald-100 text-emerald-800';
      case 'expired': return 'bg-stone-100 text-stone-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-stone-100 text-stone-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-neutral-50 rounded-xl shadow-sm border border-stone-200">
        <div className="p-6 border-b border-stone-200">
          <div className="flex justify-between items-center">

            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-indigo-600 text-neutral-50 px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
            >
              <UserPlus className="w-4 h-4" />
              Create Invitation
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Current Family Members */}
          <div className="mb-8">
            <h3 className="text-lg font-medium text-stone-900 mb-4">Current Family Members</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {family.parents?.map(parent => (
                <div key={parent.id} className="p-4 border border-stone-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      {parent.avatar && parent.avatar.match(/^https?:\/\//) ? (
                        <img src={parent.avatar} alt={parent.name} className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <span className="text-2xl">{parent.avatar}</span>
                      )}
                      <div>
                        <div className="font-medium">{parent.name}</div>
                        <div className="text-sm text-stone-500">
                          Parent {parent.isOwner && '• Owner'} {parent.hasAccount ? (
                            <span className="text-indigo-800"> • Has Account ✓</span>
                          ) : (
                            <span className="text-orange-600"> • No Account</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {!parent.hasAccount && (
                      <button
                        onClick={() => handleInviteExistingMember({...parent, role: 'parent'})}
                        className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 transition-colors text-sm flex items-center gap-1"
                      >
                        <UserPlus className="w-3 h-3" />
                        Send Invite
                      </button>
                    )}
                  </div>
                </div>
              ))}
              
              {family.kids?.map(kid => (
                <div key={kid.id} className="p-4 border border-stone-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      {kid.avatar && kid.avatar.match(/^https?:\/\//) ? (
                        <img src={kid.avatar} alt={kid.name} className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <span className="text-2xl">{kid.avatar}</span>
                      )}
                      <div>
                        <div className="font-medium">{kid.name}</div>
                        <div className="text-sm text-stone-500">
                          {kid.age && `Age ${kid.age} • `}
                          {kid.hasAccount ? (
                            <span className="text-indigo-800">Has Account ✓</span>
                          ) : (
                            <span className="text-orange-600">No Account</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {!kid.hasAccount && (
                      <button
                        onClick={() => handleInviteExistingMember(kid)}
                        className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 transition-colors text-sm flex items-center gap-1"
                      >
                        <UserPlus className="w-3 h-3" />
                        Send Invite
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pending/Recent Invitations */}
          {invitations.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-stone-900 mb-4">Invitations</h3>
              <div className="space-y-4">
                {invitations.map(invitation => (
                  <div key={invitation.id} className="p-4 border border-stone-200 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-medium text-stone-900">{invitation.invitee_name}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invitation.status)}`}>
                            {invitation.status}
                          </span>
                        </div>
                        
                        <div className="text-sm text-stone-600 space-y-1">
                          {invitation.invitee_email && (
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4" />
                              {invitation.invitee_email}
                            </div>
                          )}
                          {invitation.invitee_phone && (
                            <div className="flex items-center gap-2">
                              <MessageSquare className="w-4 h-4" />
                              {invitation.invitee_phone}
                            </div>
                          )}
                          {invitation.date_of_birth && (
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              Born {new Date(invitation.date_of_birth).toLocaleDateString()}
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            Expires {new Date(invitation.expires_at).toLocaleDateString()}
                          </div>
                        </div>

                        {invitation.status === 'pending' && (
                          <div className="mt-3 p-3 bg-gold-50 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="text-sm font-medium text-gold-900">Invite Code:</span>
                                <span className="ml-2 text-lg font-mono text-gold-700">{invitation.invite_code}</span>
                              </div>
                              <button
                                onClick={() => copyInviteCode(invitation.invite_code)}
                                className="text-gold-600 hover:text-gold-800 p-1"
                                title="Copy invite code"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {invitation.status === 'pending' && (
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => resendInvitation(invitation.id)}
                            className="text-gold-600 hover:text-gold-800 p-1"
                            title="Resend invitation"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => cancelInvitation(invitation.id)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Cancel invitation"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}

                      {invitation.status === 'accepted' && (
                        <CheckCircle className="w-5 h-5 text-indigo-800 ml-4" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Invitation Form Modals */}
      {showCreateForm && (
        <InvitationForm
          onClose={() => setShowCreateForm(false)}
          onSubmit={handleCreateInvitation}
        />
      )}

      {invitingMember && (
        <InvitationForm
          existingMember={invitingMember}
          onClose={() => setInvitingMember(null)}
          onSubmit={handleCreateInvitation}
        />
      )}
    </div>
  );
}